import { Table, TableColumn, Typography } from '@scale/scaleui';
import { ISensorFusionUnifiedProjectLookupDto } from '@scale/sensor-fusion-shared';
import { SubstageInstanceStatus, SubstageInstanceStatusValues } from '@scale/shared-types';

import { useSensorFusionLookupContext } from '../contexts/SensorFusionLookupContext';
import { CORP_LOOKUP_PATH, REMOADMIN_LOOKUP_PATH } from '../constants';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { useMemo } from 'react';

type SfuBreakdownPanelProps = {
  project: ISensorFusionUnifiedProjectLookupDto;
};

const TABLE_COLUMNS: TableColumn[] = [
  { header: 'Stage', id: 'stage' },
  { header: 'Not Started', id: SubstageInstanceStatus.NotStarted },
  { header: 'In Progress', id: SubstageInstanceStatus.InProgress },
  { header: 'Completed', id: SubstageInstanceStatus.Completed },
  { header: 'Total', id: 'total' },
];

const SfuBreakdownPanel = ({ project: { stageDefinitions } }: SfuBreakdownPanelProps) => {
  const { scaleDashboardFrontendUrl, remotasksFrontendUrl } = useSensorFusionLookupContext() ?? {};
  const { isRemoAdmin } = useRemoAdminCtx();

  const toolUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}/${REMOADMIN_LOOKUP_PATH}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}/${CORP_LOOKUP_PATH}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  const tableData = stageDefinitions.map(({ id, name, statusCounts }) => ({
    ...Object.keys(SubstageInstanceStatus).reduce((acc, key) => {
      const statusKey = SubstageInstanceStatus[key as keyof typeof SubstageInstanceStatus];
      acc[statusKey] =
        (statusCounts.claimed[statusKey] || 0) + (statusCounts.unclaimed[statusKey] || 0);
      return acc;
    }, {} as Record<SubstageInstanceStatusValues, number>),
    stage: (
      <a className="text-info-700" href={`${toolUrl}/${id}`}>
        {name}
      </a>
    ),
    total: Object.values(SubstageInstanceStatus).reduce(
      (total, status) => total + statusCounts.unclaimed[status] + statusCounts.claimed[status],
      0,
    ),
  }));

  return (
    <div className="border border-solid border-neutral-200 px-4 py-4 flex flex-col gap-2">
      <Typography.H3>Breakdown</Typography.H3>
      <Table columns={TABLE_COLUMNS} data={tableData} divideRows manualPagination />
    </div>
  );
};

export default SfuBreakdownPanel;
